/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Reset } from '@carbon/react/icons';
import { Button } from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import {
  WebChatContainer,
  WebChatInstance
} from '@ibm-watson/assistant-web-chat-react';
import usePrevious from '@ibm-watson/shared/modules/ia/hooks/usePrevious';
import { useAppSelector } from '@ibm-watson/shared/store';
import { Noop } from '../../../../ts';
import { WebChatLayoutMode } from '../../../ia/components/preview/util';
import {
  selectCsrfToken,
  selectIamId
} from '../../../ia/store/session/selectors';
import {
  selectDraftIntegration,
  selectIntegrationById
} from '../../store/channels/selectors';
import { selectWebchatSettingsByIntegrationId } from '../../store/channels/webchat-selectors';
import {
  getWebChatTabsBaseWebChatConfig,
  updateWebchatInstanceWithConfig,
  getWebchatBaseURL
} from '../../util/web-chat';
import { messages } from './messages';
import PreviewWebChatFunctions from './PreviewWebChatFunctions.d';

import './PreviewWebChat.scss';

interface PreviewWebChatProps {
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   *
   */
  className?: string;
  /**
   *
   */
  namespace: string;
  /**
   *
   */
  toolingType?: string;
  /**
   *
   */
  useDraftConfig?: boolean;
  /**
   *
   */
  isDesktopLauncherPreview?: boolean;
  /**
   *
   */
  isMobileLauncherPreview?: boolean;
  /**
   *
   */
  removeDefaultFocus?: boolean;
  /**
   *
   */
  showRestart?: boolean;
  /**
   *
   */
  disableSessionHistory?: boolean;

  /**
   * The callback function to fire before the web chat widget gets displayed.
   */
  onBeforeRender?: (instance: WebChatInstance) => Promise<void>;

  /**
   * The callback function to fire after the web chat widget gets displayed.
   */
  onAfterRender?: (instance: WebChatInstance) => Promise<void>;

  /**
   * The callback function to fire when the web chat widget has been destroyed and no longer visible on the page.
   */
  onDestroy?: () => void;

  showNotification?: Noop;
}

export const PreviewWebChat = (
  {
    integrationId,
    containerRef,
    className,
    namespace,
    toolingType,
    useDraftConfig,
    isDesktopLauncherPreview,
    isMobileLauncherPreview,
    removeDefaultFocus,
    showRestart,
    disableSessionHistory,
    onBeforeRender,
    onAfterRender,
    showNotification,
    onDestroy
  }: PreviewWebChatProps,
  ref: React.Ref<PreviewWebChatFunctions>
) => {
  const { formatMessage: t } = useIntl();

  const [webchatInstance, setWebchatInstance] = useState<any>(null);
  const [isWebChatCreated, setIsWebChatCreated] = useState<boolean>(false);
  const [webchatInstanceConfig, setWebchatInstanceConfig] = useState<any>({});

  const useLocalRef = !containerRef;
  const webChatContainer = useLocalRef ? React.createRef() : containerRef;
  const iamID = useAppSelector(selectIamId);
  const csrfToken = useAppSelector(selectCsrfToken);

  useImperativeHandle(
    ref,
    () => ({
      destroy: () => {
        webchatInstance?.destroy();
        onDestroy?.();
      }
    }),
    [onDestroy, webchatInstance]
  );

  // Config from the "draft" channel instance NOT the draft environment
  // This config is being edited and that's why it's draft
  const settingsFromDraftIntegration = useAppSelector(
    state =>
      _.omit(_.get(selectDraftIntegration(state), 'settings'), [
        'private.secrets'
      ]),
    shallowEqual
  );

  const settingsFromIntegration = useAppSelector(
    state =>
      _.omit(_.get(selectIntegrationById(state, integrationId), 'settings'), [
        'private.secrets'
      ]),
    shallowEqual
  );

  const webchatConfig = useAppSelector(
    state => selectWebchatSettingsByIntegrationId(state, integrationId),
    shallowEqual
  );

  const previousSettings = usePrevious(
    useDraftConfig ? settingsFromDraftIntegration : settingsFromIntegration
  );

  /**
   *
   */
  useEffect(() => {
    async function updateWebchatInstance() {
      if (!_.isNil(webchatInstance)) {
        const settings = useDraftConfig
          ? settingsFromDraftIntegration
          : settingsFromIntegration;
        await updateWebchatInstanceWithConfig(
          webchatInstance,
          settings,
          previousSettings
        );
        if (
          iamID &&
          !webchatConfig?.integration?.settings?.private?.authorization?.is_on
        ) {
          webchatInstance.updateUserID(iamID);
        }
      }
    }
    updateWebchatInstance();
  }, [
    iamID,
    previousSettings,
    settingsFromDraftIntegration,
    settingsFromIntegration,
    useDraftConfig,
    webchatConfig,
    webchatInstance
  ]);

  /**
   *
   */
  useEffect(() => {
    if (
      !_.isNil(webchatConfig) &&
      !_.isNil(webChatContainer) &&
      !isWebChatCreated
    ) {
      const shouldTakeFocusIfOpensAutomatically = removeDefaultFocus
        ? false
        : !(isDesktopLauncherPreview || isMobileLauncherPreview);

      setIsWebChatCreated(true);

      if (showNotification) {
        showNotification();
      }

      const { integration, plan, subscriptionID, region, serviceInstanceID } =
        webchatConfig;

      getWebChatTabsBaseWebChatConfig(
        csrfToken,
        webChatContainer.current,
        integration.integration_id,
        integration,
        namespace,
        plan,
        subscriptionID,
        region,
        serviceInstanceID,
        toolingType,
        shouldTakeFocusIfOpensAutomatically,
        null,
        true, // should auto-render
        isDesktopLauncherPreview,
        isMobileLauncherPreview,
        disableSessionHistory
      )
        .then(config => {
          if (
            config.__ibm__ &&
            !showRestart &&
            !isDesktopLauncherPreview &&
            !isMobileLauncherPreview
          ) {
            config.showRestartButton = true;
            config.__ibm__.enableFeatures = { restartConversationButton: true };
          }

          // Set web chat layout specific settings.
          switch (integration.settings.preview?.layout.mode) {
            case WebChatLayoutMode.FULLSCREEN: {
              config.layout = {
                openChatByDefault: true,
                hasContentMaxWidth: true,
                showFrame: false
              };
              config.themeConfig = {
                corners: 'square'
              };
              break;
            }
            case WebChatLayoutMode.SIDEBAR: {
              config.layout = {
                showFrame: false
              };
              config.themeConfig = {
                corners: 'square'
              };
              break;
            }
            case WebChatLayoutMode.DEFAULT:
            default:
              break;
          }

          config.onLoad = undefined;
          setWebchatInstanceConfig(config);
        })
        .catch(error => console.error(error));
    }
  }, [
    csrfToken,
    disableSessionHistory,
    isDesktopLauncherPreview,
    isMobileLauncherPreview,
    isWebChatCreated,
    namespace,
    removeDefaultFocus,
    showRestart,
    toolingType,
    webchatConfig,
    webChatContainer,
    showNotification
  ]);

  const onAfterRenderInternal = useCallback(
    async instance => {
      const settings = useDraftConfig
        ? settingsFromDraftIntegration
        : settingsFromIntegration;
      await updateWebchatInstanceWithConfig(
        instance,
        settings,
        previousSettings
      );

      if (onAfterRender) {
        await onAfterRender(instance);
      }

      setWebchatInstance(instance);
    },
    [
      previousSettings,
      settingsFromDraftIntegration,
      settingsFromIntegration,
      useDraftConfig,
      onAfterRender
    ]
  );

  return (
    <div>
      {showRestart && webchatInstance && (
        <Button
          kind="tertiary"
          iconDescription={t(messages.restartConversation)}
          size="sm"
          renderIcon={Reset}
          onClick={() => webchatInstance.restartConversation()}>
          <FormattedMessage {...messages.restartConversation} />
        </Button>
      )}
      <div className={cx('PreviewWebChat', className)}>
        {webchatInstanceConfig?.integrationID && (
          <WebChatContainer
            config={webchatInstanceConfig}
            onBeforeRender={onBeforeRender}
            onAfterRender={onAfterRenderInternal as any}
            hostURL={getWebchatBaseURL()}
          />
        )}
      </div>
    </div>
  );
};

export default React.forwardRef(PreviewWebChat);
